// src/pages/HangNhapPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import {
  collection, onSnapshot, runTransaction, doc, serverTimestamp, query,
  orderBy as fbOrderBy, addDoc, updateDoc, where
} from 'firebase/firestore';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button,
  Box, Typography, IconButton, TextField, Accordion, AccordionSummary, AccordionDetails,
  TableSortLabel, TablePagination, Tooltip, Stack, Grid, Chip
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import ImportDialog from '../components/ImportDialog';
import SupplierDialog from '../components/SupplierDialog';
import PrintDialog from '../components/PrintDialog';
import * as XLSX from 'xlsx';

// ===== Helpers =====
const toJSDate = (v) => {
  if (!v) return null;
  try {
    if (typeof v?.toDate === 'function') return v.toDate();
    if (v?.seconds) return new Date(v.seconds * 1000);
    return new Date(v);
  } catch { return null; }
};
const formatDate = (timestamp) => {
  const d = toJSDate(timestamp);
  return d ? d.toLocaleString('vi-VN') : 'N/A';
};
function descendingComparator(a, b, orderBy) {
  const av = a?.[orderBy];
  const bv = b?.[orderBy];
  const ax = typeof av?.toDate === 'function' ? av.toDate().getTime() : (av?.seconds ? av.seconds * 1000 : av);
  const bx = typeof bv?.toDate === 'function' ? bv.toDate().getTime() : (bv?.seconds ? bv.seconds * 1000 : bv);
  if ((bx ?? 0) < (ax ?? 0)) return -1;
  if ((bx ?? 0) > (ax ?? 0)) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}
const getSupplierDisplayName = (s) =>
  s?.name || s?.supplierName || s?.title || s?.displayName || '';

/** Quick range helpers (YYYY-MM-DD) */
const toYMD = (d) => d.toISOString().slice(0, 10);
const todayRange = () => {
  const t = new Date();
  return { from: toYMD(t), to: toYMD(t) };
};
const last7DaysRange = () => {
  const t = new Date();
  const from = new Date(t);
  from.setDate(t.getDate() - 6); // 7 ngày: hôm nay + 6 ngày trước
  return { from: toYMD(from), to: toYMD(t) };
};
const thisMonthRange = () => {
  const t = new Date();
  const from = new Date(t.getFullYear(), t.getMonth(), 1);
  const to = new Date(t.getFullYear(), t.getMonth() + 1, 0);
  return { from: toYMD(from), to: toYMD(to) };
};

// ===== Component =====
function HangNhapPage() {
  const [imports, setImports] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogs, setDialogs] = useState({ import: false, supplier: false, print: false });
  const [currentImport, setCurrentImport] = useState(null);
  const [orderToPrint, setOrderToPrint] = useState(null);
  const { currentUser } = useAuth();
  const { setImportCount } = useAppContext();
  const [newlyCreatedSupplier, setNewlyCreatedSupplier] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSuppliers, setSelectedSuppliers] = useState([]); // array<string>
  const [selectedProducts, setSelectedProducts] = useState([]);   // array<string>
  const [fromDate, setFromDate] = useState(''); // 'YYYY-MM-DD'
  const [toDate, setToDate] = useState('');     // 'YYYY-MM-DD'

  // Sorting & paging
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('importDate');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ==== Fetch Firestore ====
  useEffect(() => {
    const unsubProducts = onSnapshot(collection(db, 'products'), (snapshot) => {
      setProducts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubSuppliers = onSnapshot(
      collection(db, 'suppliers'),
      (snapshot) => setSuppliers(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setSuppliers([])
    );

    let q;
    if (currentUser && (currentUser.roleNames?.some(name => name.toLowerCase() === 'admin') || currentUser.permissions?.viewAllData)) {
      q = query(collection(db, 'imports'), fbOrderBy('importDate', 'desc'));
    } else if (currentUser) {
      q = query(
        collection(db, 'imports'),
        where('createdBy', '==', currentUser.email),
        fbOrderBy('importDate', 'desc')
      );
    }

    let unsubImports;
    if (q) {
      unsubImports = onSnapshot(q, (snapshot) => {
        // ✅ FIX: bỏ dấu ')' thừa ở cuối
        const importList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setImports(importList);
        setImportCount(importList.length);
        setLoading(false);
      });
    } else {
      setLoading(false);
      setImports([]);
    }

    return () => {
      unsubProducts();
      unsubSuppliers && unsubSuppliers();
      unsubImports && unsubImports();
    };
  }, [setImportCount, currentUser]);

  // ==== Dropdown options ====
  const productOptions = useMemo(() => {
    const namesFromProducts = Array.from(
      new Set(products.map(p => (p.name || p.productName || p.title || '').trim()).filter(Boolean))
    );
    const namesFromImports = Array.from(
      new Set(
        imports.flatMap(o => (o.items || []).map(i => (i.productName || '').trim()).filter(Boolean))
      )
    );
    return Array.from(new Set([...namesFromProducts, ...namesFromImports])).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [products, imports]);

  const supplierOptions = useMemo(() => {
    const namesFromSuppliers = Array.from(
      new Set(suppliers.map(getSupplierDisplayName).filter(Boolean))
    );
    const namesFromImports = Array.from(
      new Set(imports.map(o => (o.supplierName || '').trim()).filter(Boolean))
    );
    return Array.from(new Set([...namesFromSuppliers, ...namesFromImports])).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [suppliers, imports]);

  // ==== Chart data (overview, not filtered) ====
  const importStats = useMemo(() => {
    const stats = imports.reduce((acc, current) => {
      if (!current.importDate) return acc;
      const date = toJSDate(current.importDate)?.toLocaleDateString('vi-VN');
      const totalQty = (current.items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      if (!date) return acc;
      if (!acc[date]) acc[date] = { date, orders: 0, totalQuantity: 0 };
      acc[date].orders += 1;
      acc[date].totalQuantity += totalQty;
      return acc;
    }, {});
    return Object.values(stats).sort(
      (a, b) =>
        new Date(a.date.split('/').reverse().join('-')) -
        new Date(b.date.split('/').reverse().join('-'))
    );
  }, [imports]);

  // ==== Apply filters (text + MULTI dropdown + date-range) ====
  const filteredImports = useMemo(() => {
    const term = (searchTerm || '').toLowerCase();

    const selSuppliersL = (selectedSuppliers || []).map(s => s.toLowerCase().trim());
    const selProductsL  = (selectedProducts  || []).map(s => s.toLowerCase().trim());
    const hasSupplierFilter = selSuppliersL.length > 0;
    const hasProductFilter  = selProductsL.length > 0;

    const fromObj = fromDate ? new Date(`${fromDate}T00:00:00.000`) : null;
    const toObj   = toDate   ? new Date(`${toDate}T23:59:59.999`) : null;

    const matchProduct = (orderDoc) => {
      if (!hasProductFilter) return true;
      return (orderDoc.items || []).some(i => selProductsL.includes((i.productName || '').toLowerCase().trim()));
    };

    const matchSupplier = (orderDoc) => {
      if (!hasSupplierFilter) return true;
      const val = (orderDoc.supplierName || '').toLowerCase().trim();
      return selSuppliersL.includes(val);
    };

    const matchDate = (orderDoc) => {
      const d = toJSDate(orderDoc.importDate);
      if (!d) return true;
      if (fromObj && d < fromObj) return false;
      if (toObj && d > toObj) return false;
      return true;
    };

    return imports.filter(p => {
      const textOk =
        (p.poNumber && String(p.poNumber).toLowerCase().includes(term)) ||
        (p.invoiceNumber && String(p.invoiceNumber).toLowerCase().includes(term)) ||
        (p.supplierName && p.supplierName.toLowerCase().includes(term)) ||
        (p.createdBy && p.createdBy.toLowerCase().includes(term)) ||
        (p.approvedBy && p.approvedBy.toLowerCase().includes(term));

      return textOk && matchProduct(p) && matchSupplier(p) && matchDate(p);
    });
  }, [imports, searchTerm, selectedSuppliers, selectedProducts, fromDate, toDate]);

  // Sắp xếp
  const processedImports = useMemo(
    () => stableSort(filteredImports, getComparator(order, orderBy)),
    [filteredImports, order, orderBy]
  );

  // ==== Filtered stats ====
  const filteredStats = useMemo(() => {
    const hasProductFilter = (selectedProducts || []).length > 0;
    const selProductsSet = new Set(selectedProducts || []);

    const totalOrders = filteredImports.length;

    let totalQuantity = 0;
    filteredImports.forEach(o => {
      (o.items || []).forEach(i => {
        if (!hasProductFilter || selProductsSet.has(i.productName || '')) {
          totalQuantity += Number(i.quantity || 0);
        }
      });
    });

    const uniqueSuppliers = new Set(filteredImports.map(o => o.supplierName).filter(Boolean)).size;

    return { totalOrders, totalQuantity, uniqueSuppliers };
  }, [filteredImports, selectedProducts]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSaveSupplier = async (supplierData) => {
    try {
      const docRef = await addDoc(collection(db, 'suppliers'), { ...supplierData, createdAt: serverTimestamp() });
      setNewlyCreatedSupplier({ id: docRef.id, ...supplierData });
      alert('Tạo nhà cung cấp thành công!');
    } catch (error) { console.error('Lỗi khi tạo nhà cung cấp:', error); }
  };

  const handleSaveImport = async (importData) => {
    try {
      let dataForPrint;
      if (currentImport) {
        await runTransaction(db, async (transaction) => {
          const importDocRef = doc(db, 'imports', currentImport.id);

          // delta = new - old (nhập: cộng tồn)
          const deltaMap = new Map();
          (currentImport.items || []).forEach(item => {
            deltaMap.set(item.productId, (deltaMap.get(item.productId) || 0) - Number(item.quantity || 0));
          });
          (importData.items || []).forEach(item => {
            deltaMap.set(item.productId, (deltaMap.get(item.productId) || 0) + Number(item.quantity || 0));
          });

          const productRefs = Array.from(deltaMap.keys()).map(productId => doc(db, 'products', productId));
          const productDocs = await Promise.all(productRefs.map(ref => transaction.get(ref)));

          productDocs.forEach((productDoc, index) => {
            const productId = productRefs[index].id;
            const delta = Number(deltaMap.get(productId) || 0);
            if (delta !== 0) {
              if (!productDoc.exists()) throw new Error('Sản phẩm không tồn tại');
              const newQuantity = Number(productDoc.data()?.quantity || 0) + delta;
              transaction.update(productRefs[index], { quantity: newQuantity });
            }
          });

          transaction.update(importDocRef, { ...importData, updatedAt: serverTimestamp() });
        });
        dataForPrint = { ...currentImport, ...importData };
        alert('Cập nhật phiếu nhập thành công!');
      } else {
        const newImportRef = doc(collection(db, 'imports'));
        await runTransaction(db, async (transaction) => {
          const items = importData.items || [];
          const productRefs = items.map(item => doc(db, 'products', item.productId));
          const productDocs = await Promise.all(productRefs.map(ref => transaction.get(ref)));

          productDocs.forEach((productDoc, index) => {
            const item = items[index];
            if (!productDoc.exists()) throw new Error(`Sản phẩm ${item.productName} không tồn tại!`);
            const newQuantity = Number(productDoc.data()?.quantity || 0) + Number(item.quantity || 0);
            transaction.update(productRefs[index], { quantity: newQuantity });
          });

          transaction.set(newImportRef, { ...importData, importDate: serverTimestamp(), createdBy: currentUser.email });
        });
        dataForPrint = { ...importData, importDate: new Date() };
        alert('Tạo phiếu nhập thành công!');
      }

      // mở dialog in ngay sau khi lưu
      handleOpenDialog('import', false);
      setOrderToPrint(dataForPrint);
      handleOpenDialog('print', true);
    } catch (e) {
      console.error('Giao dịch thất bại: ', e);
      alert(`Đã có lỗi xảy ra: ${e.message}`);
    }
  };

  const handleDeleteImport = async (importOrder) => {
    if (!window.confirm(`Bạn có chắc muốn xóa phiếu nhập ${importOrder.poNumber}? Hành động này sẽ trừ hàng khỏi kho và không thể hoàn tác.`)) return;
    try {
      await runTransaction(db, async (transaction) => {
        const importDocRef = doc(db, 'imports', importOrder.id);
        const items = Array.isArray(importOrder.items) ? importOrder.items : [];

        const productRefs = items.map(item => doc(db, 'products', item.productId));
        const productDocs = await Promise.all(productRefs.map(ref => transaction.get(ref)));

        productDocs.forEach((productDoc, index) => {
          if (productDoc.exists()) {
            const item = items[index];
            const newQuantity = Number(productDoc.data()?.quantity || 0) - Number(item.quantity || 0);
            transaction.update(productRefs[index], { quantity: newQuantity });
          }
        });
        transaction.delete(importDocRef);
      });
      alert('Xóa phiếu nhập thành công!');
    } catch (e) {
      console.error('Lỗi xóa phiếu nhập: ', e);
      alert(`Lỗi: ${e.message}`);
    }
  };

  const handleApproveImport = async (orderId) => {
    if (!window.confirm('Bạn có chắc muốn duyệt phiếu nhập này?')) return;
    try {
      await updateDoc(doc(db, 'imports', orderId), {
        approvedBy: currentUser.email,
        approvedAt: serverTimestamp()
      });
      alert('Duyệt phiếu nhập thành công.');
    } catch (e) {
      console.error('Lỗi khi duyệt phiếu nhập: ', e);
      alert('Có lỗi xảy ra khi duyệt phiếu nhập.');
    }
  };

  const handleExportExcel = () => {
    const selProductsSet = new Set(selectedProducts || []);
    const hasProductFilter = selProductsSet.size > 0;

    const dataToExport = processedImports.flatMap(order =>
      (order.items || [])
        .filter(i => !hasProductFilter || selProductsSet.has(i.productName || ''))
        .map(item => ({
          'Ngày Nhập': formatDate(order.importDate),
          'Mã PO': order.poNumber,
          'Số Hóa Đơn': order.invoiceNumber,
          'Nhà Cung Cấp': order.supplierName || '',
          'Tên Sản Phẩm': item.productName,
          'Số Lượng': item.quantity,
          'Giá Nhập': item.importPrice,
          'Thành Tiền': Number(item.quantity || 0) * Number(item.importPrice || 0),
          'Người tạo': order.createdBy,
          'Người duyệt': order.approvedBy || 'Chưa duyệt',
        }))
    );
    if (dataToExport.length === 0) {
      alert('Không có dữ liệu để xuất.');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'PhieuNhap');
    XLSX.writeFile(wb, `Du_Lieu_Nhap_Hang_${new Date().toLocaleDateString('vi-VN').replace(/\//g, '-')}.xlsx`);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedSuppliers, selectedProducts, fromDate, toDate]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (name, state = true) => setDialogs(prev => ({ ...prev, [name]: state }));
  const handleOpenCreateDialog = () => {
    setCurrentImport(null);
    setNewlyCreatedSupplier(null);
    handleOpenDialog('import', true);
  };
  const handleOpenEditDialog = (importOrder) => {
    setCurrentImport(importOrder);
    setNewlyCreatedSupplier(null);
    handleOpenDialog('import', true);
  };

  if (loading) return <Typography>Đang tải...</Typography>;
  const isSuperUser =
    currentUser?.roleNames?.some(name => name.toLowerCase() === 'admin') ||
    currentUser?.permissions?.full_access;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Lịch Sử Nhập Hàng</Typography>
        {(isSuperUser || currentUser?.permissions?.createImports) &&
          <Button variant="contained" onClick={handleOpenCreateDialog}>Tạo Phiếu Nhập</Button>
        }
      </Box>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Thống kê & Biểu đồ</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={importStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" orientation="left" stroke="#8884d8" label={{ value: 'Tổng số lượng', angle: -90, position: 'insideLeft' }} />
              <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" label={{ value: 'Số phiếu nhập', angle: -90, position: 'insideRight' }} allowDecimals={false} />
              <RechartsTooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="totalQuantity" fill="#8884d8" name="Tổng SL nhập" />
              <Bar yAxisId="right" dataKey="orders" fill="#82ca9d" name="Số phiếu nhập" />
            </BarChart>
          </ResponsiveContainer>
        </AccordionDetails>
      </Accordion>

      {/* ===== FILTER BAR ===== */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          wrap="nowrap"
          sx={{ overflowX: { xs: 'auto', md: 'visible' } }}
        >
          {/* Search grows to fill remaining width */}
          <Grid item xs={12} md={4} sx={{ flexGrow: 1, minWidth: { md: 460, lg: 620 } }}>
            <TextField
              label="Tìm theo Mã PO, Số HĐ, Tên NCC, Người tạo, Người duyệt..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>

          {/* Product filter - MULTI */}
          <Grid item xs={12} md={3} sx={{ flexShrink: 0, minWidth: { md: 280, lg: 320 } }}>
            <Autocomplete
              multiple
              size="small"
              options={productOptions}
              value={selectedProducts}
              onChange={(_, v) => setSelectedProducts(v)}
              renderInput={(params) => <TextField {...params} label="Lọc theo TÊN SẢN PHẨM" />}
              clearOnEscape
            />
          </Grid>

          {/* Supplier filter - MULTI */}
          <Grid item xs={12} md={3} sx={{ flexShrink: 0, minWidth: { md: 280, lg: 320 } }}>
            <Autocomplete
              multiple
              size="small"
              options={supplierOptions}
              value={selectedSuppliers}
              onChange={(_, v) => setSelectedSuppliers(v)}
              renderInput={(params) => <TextField {...params} label="Lọc theo NHÀ CUNG CẤP" />}
              clearOnEscape
            />
          </Grid>

          {/* From date */}
          <Grid item xs={12} md="auto" sx={{ flexShrink: 0 }}>
            <TextField
              size="small"
              type="date"
              label="Từ ngày"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>

          {/* To date */}
          <Grid item xs={12} md="auto" sx={{ flexShrink: 0 }}>
            <TextField
              size="small"
              type="date"
              label="Đến ngày"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>

          {/* Presets */}
          <Grid item xs={12} md="auto" sx={{ flexShrink: 0, display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => { const { from, to } = todayRange(); setFromDate(from); setToDate(to); }}>
              Hôm nay
            </Button>
            <Button size="small" variant="outlined" onClick={() => { const { from, to } = last7DaysRange(); setFromDate(from); setToDate(to); }}>
              7 ngày qua
            </Button>
            <Button size="small" variant="outlined" onClick={() => { const { from, to } = thisMonthRange(); setFromDate(from); setToDate(to); }}>
              Tháng này
            </Button>
          </Grid>

          {/* Buttons */}
          <Grid
            item
            xs={12}
            md="auto"
            sx={{
              flexShrink: 0,
              display: 'flex',
              gap: 1,
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Xuất Excel
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setSearchTerm('');
                setSelectedSuppliers([]);
                setSelectedProducts([]);
                setFromDate('');
                setToDate('');
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Xóa lọc
            </Button>
          </Grid>
        </Grid>

        {/* Filtered stats */}
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip color="primary" label={`Số phiếu: ${filteredStats.totalOrders}`} />
            <Chip color="success" label={`Tổng SL nhập${selectedProducts.length ? ` (${selectedProducts.join(', ')})` : ''}: ${filteredStats.totalQuantity}`} />
            <Chip color="default" label={`Số NCC khác nhau: ${filteredStats.uniqueSuppliers}`} />
            {selectedSuppliers.length > 0 && <Chip color="warning" label={`NCC lọc: ${selectedSuppliers.join(', ')}`} />}
            {selectedProducts.length > 0 && <Chip color="secondary" label={`SP lọc: ${selectedProducts.join(', ')}`} />}
            {(fromDate || toDate) && <Chip label={`Khoảng ngày: ${fromDate || '...'} → ${toDate || '...'}`} />}
          </Stack>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'importDate'}
                    direction={orderBy === 'importDate' ? order : 'asc'}
                    onClick={() => handleRequestSort('importDate')}
                  >
                    Ngày Nhập
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'poNumber'}
                    direction={orderBy === 'poNumber' ? order : 'asc'}
                    onClick={() => handleRequestSort('poNumber')}
                  >
                    Mã PO
                  </TableSortLabel>
                </TableCell>
                <TableCell>Số Hóa Đơn</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'supplierName'}
                    direction={orderBy === 'supplierName' ? order : 'asc'}
                    onClick={() => handleRequestSort('supplierName')}
                  >
                    Nhà Cung Cấp
                  </TableSortLabel>
                </TableCell>
                <TableCell>Người tạo</TableCell>
                <TableCell>Người duyệt</TableCell>
                <TableCell align="right">Thao tác</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {processedImports
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item) => {
                  const isApproved = !!item.approvedBy;
                  const canApprove = isSuperUser || currentUser?.permissions?.approveImports;
                  const canEdit = isSuperUser || currentUser?.permissions?.editImports;
                  const canDelete = isSuperUser || currentUser?.permissions?.deleteImports;

                  return (
                    <TableRow key={item.id} hover sx={{ backgroundColor: isApproved ? 'action.hover' : 'transparent' }}>
                      <TableCell>{formatDate(item.importDate)}</TableCell>
                      <TableCell>{item.poNumber}</TableCell>
                      <TableCell>{item.invoiceNumber}</TableCell>
                      <TableCell>{item.supplierName}</TableCell>
                      <TableCell>{item.createdBy}</TableCell>
                      <TableCell>{item.approvedBy || 'Chưa duyệt'}</TableCell>

                      <TableCell align="right">
                        {/* In phiếu nhập */}
                        <Tooltip title="In Phiếu nhập">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setOrderToPrint(item);
                                handleOpenDialog('print', true);
                              }}
                            >
                              <PrintIcon />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* Duyệt */}
                        {canApprove && (
                          <Tooltip title="Duyệt phiếu">
                            <span>
                              <IconButton size="small" onClick={() => handleApproveImport(item.id)} disabled={isApproved} color="success">
                                <CheckCircleOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {/* Sửa */}
                        {canEdit && (
                          <Tooltip title={isApproved ? 'Phiếu đã duyệt không thể sửa' : 'Chỉnh sửa'}>
                            <span>
                              <IconButton size="small" onClick={() => handleOpenEditDialog(item)} disabled={isApproved}>
                                <EditIcon color={isApproved ? 'disabled' : 'warning'} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {/* Xóa */}
                        {canDelete && (
                          <Tooltip title={isApproved ? 'Phiếu đã duyệt không thể xóa' : 'Xóa phiếu'}>
                            <span>
                              <IconButton size="small" onClick={() => handleDeleteImport(item)} disabled={isApproved}>
                                <DeleteIcon color={isApproved ? 'disabled' : 'error'} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={processedImports.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          labelRowsPerPage="Số dòng mỗi trang:"
        />
      </Paper>

      <ImportDialog
        open={dialogs.import}
        onClose={() => handleOpenDialog('import', false)}
        onSave={handleSaveImport}
        products={products}
        suppliers={suppliers}
        onAddNewSupplier={() => handleOpenDialog('supplier', true)}
        newlyCreatedSupplier={newlyCreatedSupplier}
        initialImportOrder={currentImport}
      />
      <SupplierDialog
        open={dialogs.supplier}
        onClose={() => handleOpenDialog('supplier', false)}
        onSave={handleSaveSupplier}
      />
      {/* Dialog in — ép kiểu import để ẩn các nút Hợp đồng/PO/Kiểm soát */}
      <PrintDialog
        open={dialogs.print}
        onClose={() => handleOpenDialog('print', false)}
        orderData={orderToPrint}
        type="import"
      />
    </Box>
  );
}

export default HangNhapPage;
